import { useState, useMemo } from 'react';
import { IChildProp } from './type';
import { useDeepCompareEffect } from 'ahooks';
import { initFormatter, getConditionValue } from './utils/formatter';

/**
 * 获取复选框的默认值。
 *
 * @param elements - 复选框元素的数组，包含每个元素的属性。
 * @returns 返回一个字符串数组，包含所有默认选中的复选框的值。
 *
 * @example
 * const defaultValues = getCheckboxDefault([{ isDefault: true, value: 'option1', isDisable: false, isShow: true }]);
 * // defaultValues 将为 ['option1']
 */
const getCheckboxDefault = (elements: IChildProp[] = []) => {
  // console.log(elements);
  const defaultValue: string[] = [];
  elements.forEach((formItem) => {
    const { isDefault, value = '', isDisable, isShow } = formItem || {};
    if (!isDisable && isDefault && isShow) {
      defaultValue.push(value);
    }
  });
  return defaultValue;
};

/**
 * 获取默认选中的单选框值
 *
 * @param elements - 包含子元素的数组，每个元素应具有 isDefault 和 isShow 属性
 * @returns 返回第一个被选中且可显示的单选框的值，如果没有找到则返回 undefined
 */
const getRadioDefault = (elements: IChildProp[] = []) => {
  for (const formItem of elements) {
    const { isDefault, isShow } = formItem || {};
    // 出现第一个被选中则跳出循环
    if (isDefault && isShow) {
      return formItem.value;
    }
  }
};

/**
 * 获取级联选择器的默认值。
 *
 * @param elements - 级联元素数组，包含父级和子级的属性。
 * @param context - 上下文对象，包含额外字段如数据源。
 * @returns 返回一个对象，格式为 parentKey: [childKey1, childKey2, ...]，表示每个父级对应的选中子级。
 *
 * 该函数会根据父级和子级的默认选中状态，动态生成选中的子级列表。
 * 如果父级被选中，则所有子级均被选中；如果子级被选中且父级未禁用，则该子级也会被选中。
 * 在动态模式下，函数还会根据提供的数据源进一步更新结果。
 */
const getCascaderDefault = (elements: IChildProp[] = [], context: any) => {
  // console.log(elements, context);
  const sourceMap: any = {};
  const result: any = {};
  // 是否为动态模式，如果包含isDefault，则不会走下面的动态模式
  let isDynamicMode = true;
  const { dataSource } = context.additionalFields || {};
  // 根据config将result初始化为parentKey: []的格式，防止后续多重循环需要重复判断
  for (const source of elements) {
    const { value: parentKey = '' } = source;
    result[parentKey] = [];
  }
  for (const source of elements) {
    const { isDefault: parentDefault, isDisable: isParentDisable, value: parentKey = '' } = source;
    for (const metric of source?.children || []) {
      const {
        interfaceSource = '',
        source = '',
        value: childKey,
        isDefault: childDefault,
        isDisable: isChildDisable,
      } = metric || {};
      sourceMap[`${interfaceSource || source}###${childKey}`] = metric;
      // 如果父级选中，子级全部选中
      if (parentDefault && !isParentDisable) {
        result[parentKey].push(childKey);
        isDynamicMode = false;
      } else {
        // TODO：子级逻辑还未应用，待测试
        if (childDefault && !isChildDisable) {
          result[parentKey].push(childKey);
          isDynamicMode = false;
        }
      }
    }
  }
  if (dataSource && isDynamicMode) {
    for (const sourceName of Object.keys(dataSource)) {
      for (const metricName of dataSource[sourceName]) {
        const { source } = sourceMap[`${sourceName}###${metricName}`] || {};
        if (source && Object.prototype.hasOwnProperty.call(result, source)) {
          result[source].push(metricName);
        }
      }
    }
  }
  return result;
};

/**
 * 获取选项中的初始值（isDefault）
 * 之所以默认选中和是否显示分开地方写，是因为默认选中属于一次性的，组件渲染前就需要完成
 * @param type TDownloadItemType 表单类型
 * @param elements IChildProp[] 选项
 * @returns 初始值
 */
const getDefaultValue = (elements: IChildProp, context: any) => {
  const { type, children = [] } = elements;
  switch (type) {
    case 'checkboxGroup':
    case 'mutipleSelect':
    case 'textGroup':
      return getCheckboxDefault(children);
    case 'radio':
      return getRadioDefault(children);
    case 'cascader':
      return getCascaderDefault(children, context);
    default:
      break;
  }
};

/**
 * 获取自定义类型的组件初始值
 * @param elements
 * @param context
 */
const getCustomTypeDefaultValue = (elements: IChildProp, context: any) => {
  const { type } = elements;
  switch (type) {
    case 'mutipleCountry':
      return {
        value: context?.additionalFields?.markets || [],
        options: context?.additionalFields?.options || [],
      };
    default:
      break;
  }
};

/**
 * 初始化表单值
 *
 * @param config - 表单配置数组，包含各个表单项的属性
 * @param context - 上下文对象，包含额外的字段信息
 * @returns 返回初始化后的表单值对象
 *
 * 该函数根据传入的配置生成对应的表单值，支持多种类型的表单项，包括日期选择器、子表单、开关、数字输入框、文本、动态文本和多国籍选择等。
 *
 * - 对于日期选择器，返回开始和结束日期
 * - 对于子表单，递归调用自身以初始化子表单的值
 * - 对于开关，返回布尔值表示是否默认开启
 * - 对于数字输入框，处理动态值和默认值
 * - 对于文本和动态文本，返回相应的值
 * - 对于多国籍选择，调用自定义函数获取默认值
 */
const init = (config: IChildProp[] = [], context: any = {}) => {
  // console.log(config, context);
  const formValues: any = {};
  if (!config?.length) return {};
  config.forEach((element: IChildProp) => {
    const { value = '', type = 'radio', children = [], isDefault, defaultValue } = element || {};
    switch (type) {
      case 'datePicker': {
        const { dateType, startDate, endDate } = context?.additionalFields || {};
        formValues[value] = {
          type: dateType,
          value: [startDate, endDate || startDate],
        };
        break;
      }
      case 'subForm':
        formValues[value] = init(children, context);
        break;
      case 'switch':
        formValues[value] = !!isDefault;
        break;
      case 'inputNumber': {
        if (!defaultValue) {
          formValues[value] = '';
          return;
        }
        const transValue = defaultValue?.toString() || '';
        const isDynamicValue = transValue.charAt(0) === '?';
        formValues[value] = isDynamicValue ? getConditionValue(transValue, context) : transValue;
        break;
      }
      case 'text':
        formValues[value] = children[0]?.value;
        break;
      case 'dynamicText':
        // 必须由外部传入动态文本，key为dynamicText
        formValues[value] = context?.additionalFields.dynamicText;
        break;
      case 'mutipleCountry':
        formValues[value] = getCustomTypeDefaultValue(element, context);
        break;
      case 'regionCountrySelectMultiple':
        formValues[value] = context?.additionalFields?.regionCountrySelectMultiple?.value;
        break;
      default:
        formValues[value] = getDefaultValue(element, context);
        break;
    }
  });
  return {
    ...formValues,
  };
};

/**
 * 自定义 Hook，用于管理表单内容和状态。
 *
 * @param config - 表单配置项数组，定义表单的结构和字段。
 * @param additionalFields - 附加字段对象，包含额外的表单字段。
 * @returns 返回一个对象，包含格式化后的配置、表单值、附加字段以及设置和获取表单值的方法。
 *
 * @example
 * const { config, formValues, setValues, getValues } = useFormContent(configArray, { extraField: value });
 */
export default function useFormContent(config: IChildProp[], additionalFields: any = {}) {
  // 附加字段的字符串
  const addFieldsString = useMemo(() => JSON.stringify(additionalFields), [additionalFields]);
  // 当附加字段变化时，对应的值和配置要重新计算
  const addFields = useMemo(() => ({ additionalFields }), [addFieldsString]);
  const formattedConfig = useMemo(() => initFormatter(config, addFields), [addFieldsString]);
  const [formValues, setFormValue] = useState<any>(init(formattedConfig, addFields));

  useDeepCompareEffect(() => {
    setFormValue(init(formattedConfig, addFields));
  }, [addFieldsString]);

  /**
   * 设置表单字段的值。
   *
   * @param field - 要设置的字段名称。
   * @param value - 要设置的值，可以是任意类型。
   *
   * 此函数会创建表单值的深拷贝，并根据字段的当前值类型决定如何更新值。
   * 如果当前值是对象，则会合并新值；否则直接替换为新值。
   */
  const setValues = (field: string, value: any) => {
    const tmpFormValues = JSON.parse(JSON.stringify(formValues));
    const tmpValue = tmpFormValues[field];
    const isObj = Object.prototype.toString.call(tmpValue) === '[object Object]';
    tmpFormValues[field] = isObj ? Object.assign(tmpValue, value) : value;
    setFormValue(tmpFormValues);
  };

  /**
   * 获取当前表单的值。
   *
   * @returns {Object} 返回表单的当前值。
   */
  const getValues = () => formValues;

  return {
    config: formattedConfig,
    formValues,
    addFields,
    setValues,
    getValues,
  };
}
